import NotificationProxy from '@/store/proxies/notification-proxy';
import StatisticsProxy from '@/store/proxies/statistics-proxy';
import EInvoiceServiceProxy from '@/store/proxies/e-invoice-service-proxy';
import InvoiceProxy from '@/store/proxies/invoice-proxy';
import AccountProxy from '@/store/proxies/account-proxy';
import TagProxy from '@/store/proxies/tag-proxy';
import {
  AnnouncementProxy,
  SettingsProxy,
  EInvoiceProxy,
} from '@/store/proxies/e-invoice';
import { getStatisticFromInvoices } from '@/utils/invoice';
import DateTimeUtils from '@/utils/datetime';
import NumericUtils from '@/utils/numeric';
import {
  addRecentImportedInvoices,
  removeRecentImportedInvoices,
  mergeObject,
  removeKeys,
} from '@/utils/local-storage';

import { PAGE_NOTIFICATION_LIMIT, PAGE_LIMIT } from '@/app-constant';
import * as types from './mutation-types';

const onSuccess = (commit, resolve, actionType) => (result) => {
  commit(actionType, result);
  resolve(result);
};

export const updateInvoiceInInvoiceList = ({ commit }, payload) => {
  commit(types.UPDATE_INVOICE_FIELD, payload);
};

export const setState = ({ commit }, payload) => {
  commit(types.SET_STATE, payload);
};

export const updateAppDirectory = ({ commit }, payload) => {
  commit(types.UPDATE_APP_DIRECTORY, payload);
};

export const onCheckInvoiceChanged = ({ commit }, payload) => {
  commit(types.INVOICE_CHECK_ITEM_CHANGED, payload);
};
export const resetCheckInvoice = ({ commit }, payload) => {
  commit(types.RESET_CHECK_INVOICE, payload);
};

export const loadInvoiceFile = ({ commit }, invoiceId) => {
  if (invoiceId) {
    commit(types.INVOICE_FILE_CHANGED, '');
    commit(types.INVOICE_FILE_LOADING, true);
    const promise = new InvoiceProxy().getInvoiceFile(invoiceId);
    promise.then((res) => {
      commit(types.INVOICE_FILE_CHANGED, res.file_base64);
      setTimeout(() => {
        commit(types.INVOICE_FILE_LOADING, false);
      });
    });
  }
};

export const getDetailInvoice = ({ commit }, payload) => new Promise((resolve, reject) => {
  commit(types.DETAILS_INVOICE_LOADING, true);
  const params = {
    ...payload,
    return_xml: false,
  };
  new AccountProxy()
    .getDetailsInvoice(params)
    .then((res) => {
      commit(types.GET_DETAILS_INVOICE, res.data);
      setTimeout(() => {
        commit(types.DETAILS_INVOICE_LOADING, false);
        resolve(res);
      }, 300);
    })
    .catch((e) => {
      commit(types.DETAILS_INVOICE_LOADING, false);
      commit(types.GET_DETAILS_INVOICE, { id: payload.id, content: null });
      reject(e);
    });
});

export const downloadInvoice = ({ commit }, payload) => new Promise((resolve, reject) => {
  commit(types.DOWNLOAD_INVOICE_LOADING, true);
  const params = {
    ...payload,
    return_xml: true,
  };
  const options = {
    responseType: 'blob',
    showResponseHeaders: true,
  };
  new AccountProxy()
    .getDetailsInvoice(params, options)
    .then(res => resolve(res))
    .catch(e => reject(e))
    .finally(() => {
      setTimeout(() => {
        commit(types.DOWNLOAD_INVOICE_LOADING, false);
      }, 300);
    });
});

export const getInvoices = ({ commit }, payload) => new Promise((resolve, reject) => {
  commit(types.INVOICE_LIST_LOADING, true);
  const defaultParams = {
    start_date: null,
    end_date: null,
    tag_id: null,
    invoice_outgoing: null,
    is_secure: null,
    invoice_virtual: null,
    search_text: null,
    page: null,
  };
  const params = {
    ...defaultParams,
    page_size: PAGE_LIMIT,
    start_date: DateTimeUtils.formatDate(payload.start_date, 'YYYY-MM-DD'),
    end_date: DateTimeUtils.formatDate(payload.end_date, 'YYYY-MM-DD'),
    ...payload,
  };

  new InvoiceProxy(params)
    .getInvoices()
    .then((res) => {
      commit(types.GET_INVOICES, res.data);
      commit(types.INVOICE_LIST_PAGE_INFO, res.page_info);
      resolve(res);
    })
    .catch(e => reject(e))
    .finally(() => {
      setTimeout(() => {
        commit(types.INVOICE_LIST_LOADING, false);
      }, 300);
    });
});

export const getInvoicesIssuing = ({ commit }, payload) => new Promise((resolve, reject) => {
  commit(types.INVOICE_LIST_LOADING, true);
  const defaultParams = {
    start_date: null,
    end_date: null,
    tag_id: null,
    is_signed: null,
    search_text: null,
    page: null,
  };
  const params = {
    ...defaultParams,
    page_size: PAGE_LIMIT,
    start_date: DateTimeUtils.formatDate(payload.start_date, 'YYYY-MM-DD'),
    end_date: DateTimeUtils.formatDate(payload.end_date, 'YYYY-MM-DD'),
    ...payload,
  };

  new InvoiceProxy(params)
    .getInvoicesReleasing()
    .then((res) => {
      commit(types.GET_INVOICES_ISSUING, res.data);
      commit(types.INVOICE_LIST_PAGE_INFO, res.page_info);
      resolve(res);
    })
    .catch(e => reject(e))
    .finally(() => {
      setTimeout(() => {
        commit(types.INVOICE_LIST_LOADING, false);
      }, 300);
    });
});

export const getCustomers = ({ commit }, payload) => new Promise((resolve, reject) => {
  commit(types.CUSTOMER_LIST_LOADING, true);
  const defaultParams = {
    search_text: null,
    debt: null,
    page: null,
  };
  const params = {
    ...defaultParams,
    type: 'customers',
    page_size: PAGE_LIMIT,
    start_date: DateTimeUtils.formatDate(payload.start_date, 'YYYY-MM-DD'),
    end_date: DateTimeUtils.formatDate(payload.end_date, 'YYYY-MM-DD'),
    ...payload,
  };

  new StatisticsProxy()
    .getCompanies(params)
    .then((res) => {
      commit(types.GET_CUSTOMERS, res.data);
      resolve(res);
    })
    .catch(e => reject(e))
    .finally(() => {
      setTimeout(() => {
        commit(types.CUSTOMER_LIST_LOADING, false);
      }, 300);
    });
});

export const getVendors = ({ commit }, payload) => new Promise((resolve, reject) => {
  commit(types.VENDOR_LIST_LOADING, true);
  const defaultParams = {
    search_text: null,
    debt: null,
    page: null,
  };
  const params = {
    ...defaultParams,
    type: 'vendors',
    page_size: PAGE_LIMIT,
    start_date: DateTimeUtils.formatDate(payload.start_date, 'YYYY-MM-DD'),
    end_date: DateTimeUtils.formatDate(payload.end_date, 'YYYY-MM-DD'),
    ...payload,
  };

  new StatisticsProxy()
    .getCompanies(params)
    .then((res) => {
      commit(types.GET_VENDORS, res.data);
      resolve(res);
    })
    .catch(e => reject(e))
    .finally(() => {
      setTimeout(() => {
        commit(types.VENDOR_LIST_LOADING, false);
      }, 300);
    });
});

export const getInProgressInvoices = ({ commit }, withInMinutes) => new Promise((resolve, reject) => {
  const inProgressInvoiceList = JSON.parse(sessionStorage.getItem('inProgressInvoiceList'));
  if (inProgressInvoiceList && withInMinutes !== 0) {
    const minutes = DateTimeUtils.diff(new Date(inProgressInvoiceList.time), new Date(), 'minutes');
    if (minutes < withInMinutes) {
      resolve(inProgressInvoiceList.items);
      return;
    }
  }
  new InvoiceProxy().getInvoices({
    status: 'WAIT_FOR_APPROVAL',
    tags: null,
    directories: [],
    from_date: null,
    to_date: null,
    based_on_paid: false,
  })
  .then((res) => {
    sessionStorage.setItem('inProgressInvoiceList', JSON.stringify({
      items: res.invoices,
      time: Date.now(),
    }));
    resolve(res.invoices);
  })
  .catch(error => reject(error));
});


export const getPartnerInvoices = ({ commit }, payload) => new Promise((resolve, reject) => {
  if (payload.page === 1) {
    commit(types.INVOICE_LIST_LOADING, true);
  }
  new InvoiceProxy()
    .getPartnerInvoices(payload)
    .then((res) => {
      commit(types.GET_INVOICES, res.invoices);
      resolve(res);
    })
    .catch(e => reject(e))
    .finally(() => {
      if (payload.page === 1) {
        setTimeout(() => {
          commit(types.INVOICE_LIST_LOADING, false);
        }, 300);
      }
    });
});

export const loadMoreInvoices = ({ commit }, payload) => new Promise((resolve, reject) => {
  const defaultParams = {
    start_date: null,
    end_date: null,
    tag_id: null,
    invoice_outgoing: null,
    is_secure: null,
    invoice_virtuzal: null,
    search_text: null,
    page: null,
  };
  const params = {
    ...defaultParams,
    page_size: PAGE_LIMIT,
    start_date: DateTimeUtils.formatDate(payload.start_date, 'YYYY-MM-DD'),
    end_date: DateTimeUtils.formatDate(payload.end_date, 'YYYY-MM-DD'),
    ...payload,
  };
  new InvoiceProxy(params).getInvoices().then((res) => {
    commit(types.LOAD_MORE_INVOICES, res.data);
    resolve(res);
  }).catch(e => reject(e));
});

export const loadMoreInvoicesIssuing = ({ commit }, payload) => new Promise((resolve, reject) => {
  const defaultParams = {
    start_date: null,
    end_date: null,
    tag_id: null,
    is_signed: null,
    search_text: null,
    page: null,
  };
  const params = {
    ...defaultParams,
    page_size: PAGE_LIMIT,
    start_date: DateTimeUtils.formatDate(payload.start_date, 'YYYY-MM-DD'),
    end_date: DateTimeUtils.formatDate(payload.end_date, 'YYYY-MM-DD'),
    ...payload,
  };
  new InvoiceProxy(params).getInvoicesReleasing().then((res) => {
    commit(types.LOAD_MORE_INVOICES, res.data.invoices_list);
    resolve(res);
  }).catch(e => reject(e));
});

export const loadMoreCustomers = ({ commit }, payload) => new Promise((resolve, reject) => {
  const defaultParams = {
    search_text: null,
    debt: null,
    page: null,
  };
  const params = {
    ...defaultParams,
    type: 'customers',
    page_size: PAGE_LIMIT,
    start_date: DateTimeUtils.formatDate(payload.start_date, 'YYYY-MM-DD'),
    end_date: DateTimeUtils.formatDate(payload.end_date, 'YYYY-MM-DD'),
    ...payload,
  };

  new StatisticsProxy().getCompanies(params).then((res) => {
    commit(types.LOAD_MORE_CUSTOMERS, res.data);
    resolve(res);
  }).catch(e => reject(e));
});

export const loadMoreVendors = ({ commit }, payload) => new Promise((resolve, reject) => {
  const defaultParams = {
    search_text: null,
    debt: null,
    page: null,
  };
  const params = {
    ...defaultParams,
    type: 'vendors',
    page_size: PAGE_LIMIT,
    start_date: DateTimeUtils.formatDate(payload.start_date, 'YYYY-MM-DD'),
    end_date: DateTimeUtils.formatDate(payload.end_date, 'YYYY-MM-DD'),
    ...payload,
  };

  new StatisticsProxy().getCompanies(params).then((res) => {
    commit(types.LOAD_MORE_VENDORS, res.data);
    resolve(res);
  }).catch(e => reject(e));
});

export const filterInvoices = ({ commit }, payload) => new Promise((resolve) => {
  new InvoiceProxy().searchInvoices(payload.searchText).then((res) => {
    commit(types.FILTER_INVOICES, {
      filterParams: payload,
      invoices: res.invoices,
    });
    resolve(res.invoices);
  });
});

export const updateInvoice = ({ commit }, payload) => new Promise((resolve, reject) => {
  new InvoiceProxy().updateInvoice(payload).then(onSuccess(commit, resolve, types.UPDATE_INVOICE)).catch(e => reject(e));
});

export const createInvoice = ({ commit }, payload) => new Promise((resolve, reject) => {
  new InvoiceProxy().createInvoice(payload).then(onSuccess(commit, resolve, types.CREATE_INVOICE)).catch(e => reject(e));
});

export const createNewInvoiceByForm = ({ commit }, payload) =>
  new Promise((resolve, reject) => {
    new EInvoiceServiceProxy().createEInvoiceService(payload)
      .then(onSuccess(commit, resolve, types.CREATE_INVOICE))
      .catch(e => reject(e));
  });

export const approveListInvoice = ({ commit }, payload) =>
  new Promise((resolve, reject) => {
    new InvoiceProxy()
      .approveListInvoice(payload)
      .then(onSuccess(commit, resolve, types.APPROVE_LIST_INVOICE))
      .catch(e => reject(e));
  });

export const getTags = ({ commit }) =>
  new Promise((resolve) => {
    const cachedItem = localStorage.getItem('tags');
    if (cachedItem) {
      const result = JSON.parse(cachedItem);
      const hasValidData = Array.isArray(result) && result.length > 0;

      if (hasValidData) {
        commit(types.GET_TAGS, result);
        resolve(result);
      }
    } else {
      new TagProxy().getTags().then(onSuccess(commit, resolve, types.GET_TAGS));
    }
  });

export const getInvoiceListEnum = ({ commit }) =>
  new Promise((resolve) => {
    const cachedItem = localStorage.getItem('list-enum');
    if (cachedItem) {
      const result = JSON.parse(cachedItem);
      const hasValidData =
        Array.isArray(result.CurrencyCodeEnum) && result.CurrencyCodeEnum.length > 0 &&
        Array.isArray(result.PaymentMethodEnum) && result.PaymentMethodEnum.length > 0 &&
        Array.isArray(result.IdentifyTypeEnum) && result.IdentifyTypeEnum.length > 0 &&
        Array.isArray(result.InvoiceLineTypeEnum) && result.InvoiceLineTypeEnum.length > 0;

      if (hasValidData) {
        commit(types.GET_INVOICE_LIST_ENUM, result);
        resolve(result);
      }
    } else {
      new SettingsProxy().getSettingListEnum().then(onSuccess(commit, resolve, types.GET_INVOICE_LIST_ENUM));
    }
  });

export const getSellerSettingList = ({ commit }) =>
  new Promise((resolve) => {
    const cachedItem = localStorage.getItem('seller-setting-list');
    if (cachedItem) {
      const result = JSON.parse(cachedItem);
    
      const hasValidData =
        Array.isArray(result.qty_rate__settings) && result.qty_rate__settings.length > 0 &&
        Array.isArray(result.on_detail_invoice__settings) && result.on_detail_invoice__settings.length > 0 &&
        Array.isArray(result.on_invoice__settings) && result.on_invoice__settings.length > 0;
    
      if (hasValidData) {
        commit(types.GET_CACHED_SELLER_SETTING_LIST, result);
        resolve(result);
      }
    } else {
      new SettingsProxy().getSellerSettingList().then(onSuccess(commit, resolve, types.GET_SELLER_SETTING_LIST));
    }
    // new SettingsProxy().getSellerSettingList().then(onSuccess(commit, resolve, types.GET_SELLER_SETTING_LIST));
  });

export const getInvoiceCommonCode = ({ commit }) =>
  new Promise((resolve) => {
    const cachedItem = localStorage.getItem('invoice_common_code');
    if (cachedItem) {
      const result = JSON.parse(cachedItem);
      const hasValidData = Array.isArray(result) && result.length > 0;

      if (hasValidData) {
        commit(types.GET_INVOICE_COMMON_CODE, result);
        resolve(result);
      }
    } else {
      new EInvoiceServiceProxy().getActiveSeries().then(onSuccess(commit, resolve, types.GET_INVOICE_COMMON_CODE));
    }
  });

export const getActiveAnnouncementLines = ({ commit }) =>
  new Promise((resolve) => {
    const cachedItem = localStorage.getItem('active-announcement-lines');
    if (cachedItem) {
      const result = JSON.parse(cachedItem);

      const hasValidData =
        Array.isArray(result.list_invoice_type) && result.list_invoice_type.length > 0 &&
        typeof result.list_template_code === 'object' && Object.keys(result.list_template_code).length > 0 &&
        typeof result.list_common_code === 'object' && Object.keys(result.list_common_code).length > 0 &&
        typeof result.list_template_announcement === 'object' && Object.keys(result.list_template_announcement).length > 0;

      if (hasValidData) {
        commit(types.GET_CACHED_ACTIVE_ANNOUNCEMENT_LINES, result);
        resolve(result);
      }
    } else {
      new AnnouncementProxy().getActiveAnnouncementLines().then(onSuccess(commit, resolve, types.GET_ACTIVE_ANNOUNCEMENT_LINES));
    }
  });

export const deleteTag = ({ commit }, id) =>
  new Promise((resolve) => {
    new TagProxy().deleteTag(id).then(() => {
      commit(types.DELETE_TAG, id);
      commit(types.INVOICE_FILE_CHANGED, null);
      commit(types.GET_INVOICES, []);
      resolve(id);
    });
  });

export const updateTag = ({ commit }, payload) =>
  new Promise((resolve) => {
    new TagProxy()
      .updateTag(payload)
      .then(onSuccess(commit, resolve, types.UPDATE_TAG));
  });

export const createTag = ({ commit }, payload) =>
  new Promise((resolve) => {
    new TagProxy()
      .createTag(payload)
      .then(onSuccess(commit, resolve, types.CREATE_TAG));
  });

export const moveInvoiceToDir = ({ commit }, payload) =>
  new Promise((resolve) => {
    new InvoiceProxy().moveToDirectory(payload).then(() => {
      resolve(payload);
    });
  });

export const setPaidDate = ({ commit }, payload) =>
  new Promise((resolve) => {
    new InvoiceProxy().setPaidDate(payload).then((res) => {
      commit(types.SET_PAID_DATE, res);
      resolve(res);
    });
  });

export const setImportedInvoices = ({ commit }, invoices) =>
  new Promise((resolve) => {
    const inv = addRecentImportedInvoices(invoices);
    commit(types.SET_IMPORTED_INVOICES, inv);
    resolve(inv);
  });

export const addImportedInvoice = ({ commit }, invoice) =>
  new Promise((resolve) => {
    const inv = addRecentImportedInvoices([invoice]);
    resolve(inv);
    commit(types.SET_IMPORTED_INVOICES, inv);
  });

export const removeImportedInvoice = ({ commit }, invoice) =>
  new Promise((resolve) => {
    const inv = removeRecentImportedInvoices(invoice);
    resolve(inv);
    commit(types.SET_IMPORTED_INVOICES, inv);
  });

export const cloneInvoice = ({ commit }, payload) =>
  new Promise((resolve) => {
    new InvoiceProxy().cloneInvoice(payload).then((response) => {
      const inv = addRecentImportedInvoices(response.filter(r => !!r.id));
      commit(types.CLONE_INVOICES, inv);
      resolve({ response, importedInvoices: inv });
    });
  });

export const setTag = ({ commit }, payload) =>
  new Promise((resolve) => {
    new InvoiceProxy().setTag(payload).then(() => {
      resolve(payload);
      commit(types.SET_TAG, payload);
    });
  });

export const setAutoCompletedOptions = ({ commit }, payload) => {
  commit('SET_AUTOCOMPLETE_OPTIONS', payload);
};
export const setInvoiceListEnum = ({ commit }, payload) => {
  commit('SET_INVOICE_LIST_ENUM', payload);
};

const formatAmount = number => NumericUtils.formatNumber(number, 'currency');

export const getTagsStatistic = ({ commit }, payload) =>
  new Promise((resolve) => {
    const getStatisticApi = new StatisticsProxy().getTagsStatistic(payload);
    getStatisticApi.then((res) => {
      const result = {};
      res.forEach((i) => {
        result[i.id] = {
          ...i,
          expense: formatAmount(i.expense),
          revenue: formatAmount(i.revenue),
        };
      });
      commit(types.GET_APP_STATISTIC, {
        tags: result,
      });
      resolve(res.data);
    });
  });

export const getPartnerStatistic = ({ commit }, invoices) =>
  new Promise((resolve) => {
    const statistic = getStatisticFromInvoices(invoices);
    commit(types.SET_CURRENT_STATISTIC, statistic);
    resolve(statistic);
  });

export const getInvoicesStatistic = ({ commit }, payload) => new Promise((resolve, reject) => {
  const params = {
    ...payload,
    start_date: DateTimeUtils.formatDate(payload.start_date, 'YYYY-MM-DD'),
    end_date: DateTimeUtils.formatDate(payload.end_date, 'YYYY-MM-DD'),
  };

  new StatisticsProxy()
    .getInvoicesStatistic(params)
    .then(onSuccess(commit, resolve, types.GET_INVOICES_STATISTIC))
    .catch(e => reject(e));
});

export const getNotifications = ({ commit }, payload) => new Promise((resolve, reject) => {
  commit(types.NOTIFICATIONS_LOADING, true);
  const defaultParams = {
    page: undefined,
    is_readed: undefined,
  };
  const params = {
    ...defaultParams,
    page_size: PAGE_NOTIFICATION_LIMIT,
    ...payload,
  };
  new NotificationProxy()
      .getNotifications(params)
      .then((res) => {
        commit(params.is_readed === false
          ? types.GET_UNREAD_NOTIFICATIONS
          : types.GET_NOTIFICATIONS
          , res);
        resolve(res);
      })
      .catch(e => reject(e))
      .finally(() => {
        setTimeout(() => {
          commit(types.NOTIFICATIONS_LOADING, false);
        }, 300);
      });
});

export const loadMoreNotifications = ({ commit }, payload) => new Promise((resolve, reject) => {
  commit(types.NOTIFICATIONS_LOADING, true);
  const defaultParams = {
    page: undefined,
    is_readed: undefined,
  };
  const params = {
    ...defaultParams,
    page_size: PAGE_NOTIFICATION_LIMIT,
    ...payload,
  };
  new NotificationProxy()
    .getNotifications(params)
    .then((res) => {
      commit(params.is_readed === false
        ? types.LOAD_MORE_UNREAD_NOTIFICATIONS
        : types.LOAD_MORE_NOTIFICATIONS
        , res);
      resolve(res);
    })
    .catch(e => reject(e))
    .finally(() => {
      setTimeout(() => {
        commit(types.NOTIFICATIONS_LOADING, false);
      }, 300);
    });
});

export const markAsRead = ({ commit }, payload) =>
  new Promise((resolve, reject) => {
    const params = {
      notification_ids: [
        ...payload,
      ],
    };
    new NotificationProxy().readNotification(params)
      .then((res) => {
        commit(types.MARK_READ_NOTIFICATION, params.notification_ids);
        resolve(res.data);
      })
      .catch(e => reject(e));
  });

export const addErrorInvoices = ({ commit }, payload) => {
  const errorInvoices = mergeObject('inProgressErrorInvoices', payload);
  commit(types.SET_ERROR_INVOICES, errorInvoices);
};

export const removeErrorInvoices = ({ commit }, keys) => {
  const errorInvoices = removeKeys('inProgressErrorInvoices', keys);
  commit(types.SET_ERROR_INVOICES, errorInvoices);
};

export const checkValidCustomer = ({ commit }, payload) =>
  new Promise((resolve, reject) => {
    new InvoiceProxy().checkValidCustomer(payload).then((res) => {
      resolve(res);
    }).catch(e => reject(e));
  });

export const downloadExcel = ({ commit }, payload) => new Promise((resolve, reject) => {
  commit(types.DOWNLOAD_EXCEL_LOADING, true);
  new InvoiceProxy()
    .exportExcelInconsistencyInvoices(payload)
    .then(res => resolve(res))
    .catch(e => reject(e))
    .finally(() => {
      setTimeout(() => {
        commit(types.DOWNLOAD_EXCEL_LOADING, false);
      }, 300);
    });
});

export const downloadXmlBcss = ({ commit }, payload) => new Promise((resolve, reject) => {
  commit(types.DOWNLOAD_XML_BCSS_LOADING, true);
  new EInvoiceProxy()
    .downloadXmlBcssHistory(payload)
    .then(res => resolve(res))
    .catch(e => reject(e))
    .finally(() => {
      setTimeout(() => {
        commit(types.DOWNLOAD_XML_BCSS_LOADING, false);
      }, 300);
    });
});

export default {
  setState,
  updateInvoiceInInvoiceList,
  updateAppDirectory,
  onCheckInvoiceChanged,
  resetCheckInvoice,
  loadInvoiceFile,
  downloadInvoice,
  getDetailInvoice,
  getInvoices,
  getInvoicesIssuing,
  getCustomers,
  getVendors,
  getInProgressInvoices,
  getPartnerInvoices,
  loadMoreInvoices,
  loadMoreInvoicesIssuing,
  loadMoreCustomers,
  loadMoreVendors,
  createNewInvoiceByForm,
  approveListInvoice,
  createInvoice,
  updateInvoice,
  getInvoiceListEnum,
  getSellerSettingList,
  getInvoiceCommonCode,
  getActiveAnnouncementLines,
  deleteTag,
  updateTag,
  createTag,
  moveInvoiceToDir,
  setTag,
  setAutoCompletedOptions,
  filterInvoices,
  getTagsStatistic,
  getTags,
  getNotifications,
  loadMoreNotifications,
  markAsRead,
  cloneInvoice,
  setPaidDate,
  setImportedInvoices,
  addImportedInvoice,
  removeImportedInvoice,
  addErrorInvoices,
  removeErrorInvoices,
  getPartnerStatistic,
  getInvoicesStatistic,
  checkValidCustomer,
  downloadExcel,
  downloadXmlBcss,
};



// WEBPACK FOOTER //
// ./src/store/actions.js